import { useState } from 'react'
import {
  FormattedDate,
  FormattedMessage,
  FormattedRelativeTime
} from 'react-intl'
import { getRelativeTimeProps, shouldUseRelativeTime } from '@utils/dateUtils'
import AvatarPlaceholder from '@components/AvatarPlaceholder/AvatarPlaceholder'
import { useQueryCRUD, useQueryInfo } from '@hooks/useQuery'
import { useNavigate } from 'react-router'
import { useResourcePermissions } from '@hooks/useResourcePermissions'
import { useTrialGuard } from '@hooks/useTrialGuard'
import PrivacyTag from '@components/PrivacyTag/PrivacyTag'

export default function QueryInfo({
  id,
  onRun,
  isLoading
}: {
  id: string
  onRun: () => void
  isLoading: boolean
}) {
  const item = useQueryInfo(id)
  const navigate = useNavigate()
  const [isCloning, setIsCloning] = useState(false)
  const { createQuery } = useQueryCRUD()
  const { canEdit } = useResourcePermissions(item?.owner.id)
  const { guard } = useTrialGuard()

  if (!item) return null

  const lastRunProps = item.lastRun && getRelativeTimeProps(item.lastRun)
  const createdIsRecent = shouldUseRelativeTime(item.created)
  const createdProps = createdIsRecent
    ? getRelativeTimeProps(item.created)
    : null

  const handleClone = async () => {
    if (!item) return

    setIsCloning(true)
    try {
      const response = await createQuery({
        title: `${item.title} (Copia)`,
        blueprint: item.blueprint,
        public: item.public
      })
      if (response?.ok && response?.query?.id) {
        navigate(`/query-builder/${response.query.id}`)
      } else {
        console.error('Error en la respuesta al clonar:', response)
      }
    } catch (error) {
      console.error('clone:', error)
    } finally {
      setIsCloning(false)
    }
  }
  return (
    <>
      <header id="query-info-header">
        <h1 title={item.title} className="query-title">
          {item.title}
        </h1>
        <PrivacyTag privacy={item.public ? 'public' : 'private'} />
      </header>
      <div id="query-info">
        <div className="query-meta">
          <div className="meta-text username">
            <AvatarPlaceholder className="user-avatar" text={item.owner.name} />
            <span>{item.owner.name}</span>
          </div>
          <div className="meta-text last-run">
            <span className="icon icon-clock" />
            <span>
              <FormattedMessage
                id="query-item.lastUpdate"
                values={{
                  time: lastRunProps ? (
                    <FormattedRelativeTime
                      key="item"
                      value={lastRunProps.value}
                      unit={lastRunProps.unit}
                      numeric="auto"
                    />
                  ) : (
                    <FormattedMessage key="item" id="never" />
                  )
                }}
              />
            </span>
          </div>
          <div className="meta-text created">
            <span className="icon icon-calendar" />
            <span>
              <FormattedMessage
                id="query-item.created"
                values={{
                  time:
                    createdIsRecent && createdProps ? (
                      <FormattedRelativeTime
                        key="item"
                        value={createdProps.value}
                        unit={createdProps.unit}
                      />
                    ) : (
                      <FormattedDate
                        key="item"
                        value={item.created}
                        year="numeric"
                        month="short"
                        day="numeric"
                      />
                    )
                }}
              />
            </span>
          </div>
        </div>
        <div className="query-actions">
          <button
            className="highlight-button"
            onClick={guard(onRun)}
            disabled={isLoading}
          >
            <span
              className={`icon ${isLoading ? 'icon-play-filled' : 'icon-play-stroke'}`}
            />
            <span>
              <FormattedMessage id={isLoading ? 'running' : 'run'} />
            </span>
          </button>
          <button
            disabled={isCloning}
            onClick={guard(handleClone)}
            className="standart-button"
          >
            <span className="icon icon-copy" />
            <span>
              <FormattedMessage id="clone" />
            </span>
          </button>
          <button
            onClick={() =>
              navigate(
                canEdit
                  ? `/query-builder/${id}`
                  : `/query-builder/${id}?mode=view`
              )
            }
            className="standart-button"
          >
            <span className={`icon ${canEdit ? 'icon-pen' : 'icon-eye'}`} />
            <span>
              <FormattedMessage id={canEdit ? 'edit' : 'view'} />
            </span>
          </button>
        </div>
      </div>
    </>
  )
}
